import { useEffect, useState, useCallback } from 'react';

export const useOptimizedAnimations = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [isLowPowerMode, setIsLowPowerMode] = useState(false);

  useEffect(() => {
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check for reduce motion preference
    const checkReduceMotion = () => {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setReduceMotion(mediaQuery.matches);
      
      const handleChange = (e) => setReduceMotion(e.matches);
      mediaQuery.addEventListener('change', handleChange);
      
      return () => mediaQuery.removeEventListener('change', handleChange);
    };
    
    // Check for low power mode (battery level)
    const checkBattery = async () => {
      if ('getBattery' in navigator) {
        try {
          const battery = await navigator.getBattery();
          setIsLowPowerMode(battery.level < 0.2 && !battery.charging);
          
          battery.addEventListener('levelchange', () => {
            setIsLowPowerMode(battery.level < 0.2 && !battery.charging);
          });
        } catch (e) {
          // Battery API not supported
        }
      }
    };
    
    checkMobile();
    const cleanup = checkReduceMotion();
    checkBattery();
    
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      cleanup?.();
    };
  }, []);

  const shouldReduceAnimations = isMobile || reduceMotion || isLowPowerMode;
  
  const getAnimationConfig = useCallback((defaultConfig) => {
    if (shouldReduceAnimations) {
      return {
        ...defaultConfig,
        duration: defaultConfig.duration ? defaultConfig.duration * 0.5 : 0.2,
        delay: 0,
        staggerChildren: 0,
      };
    }
    return defaultConfig;
  }, [shouldReduceAnimations]);

  return {
    isMobile,
    reduceMotion,
    isLowPowerMode,
    shouldReduceAnimations,
    getAnimationConfig,
  };
};

// Debounce hook for scroll and resize events
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Throttle hook for frequent events
export const useThrottle = (callback, delay) => {
  const [lastRun, setLastRun] = useState(Date.now());

  return useCallback((...args) => {
    const now = Date.now();
    if (now - lastRun >= delay) {
      setLastRun(now);
      callback(...args);
    }
  }, [callback, delay, lastRun]);
};